import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Users,
  UserCheck,
  LogIn,
  Activity,
  ShieldCheck,
  ShieldAlert,
  Search,
  Filter,
  RefreshCw,
  Download,
  Clock,
  Globe,
  Monitor,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Copy,
  Check,
} from 'lucide-react'
import { Button, Input, Select, Modal, Badge } from '../atoms'
import { useApi } from '../../store/auth.store'
import { useSuccessToast, useErrorToast } from '../atoms/Toaster'

interface ActivitySummary {
  totalUsers: number
  activeUsers: number
  inactiveUsers: number
  usersWith2FA: number
  loggedInToday: number
  logins24h: number
  totalLogs: number
  recentLogsCount: number
}

interface ActivityUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  isActive: boolean
  twoFactorEnabled: boolean
  lastLogin: string | null
  createdAt: string
  area?: { id: string; name: string } | null
  lastLoginIp: string | null
  lastLoginUserAgent: string | null
  totalActivities: number
}

interface AuditLogItem {
  id: string
  userId: string | null
  action: string
  entityType: string
  entityId: string | null
  mailboxId: string | null
  changes: any
  ipAddress: string | null
  userAgent: string | null
  timestamp: string
  metadata: any
  user?: {
    id: string
    firstName: string
    lastName: string
    email: string
    role: string
    avatarUrl: string | null
  } | null
}

function parseUserAgent(ua?: string | null): { browser: string; os: string } {
  if (!ua || ua === 'unknown') return { browser: 'Desconocido', os: 'Desconocido' }
  let browser = 'Navegador Web'
  let os = 'Dispositivo'

  if (ua.includes('Edg/')) browser = 'Edge'
  else if (ua.includes('Chrome/')) browser = 'Chrome'
  else if (ua.includes('Firefox/')) browser = 'Firefox'
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari'
  else if (ua.includes('Postman')) browser = 'Postman'
  else if (ua.includes('curl')) browser = 'cURL'

  if (ua.includes('Windows NT 10.0') || ua.includes('Windows NT 11.0') || ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Macintosh') || ua.includes('Mac OS')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

  return { browser, os }
}

function formatRelativeTime(dateStr?: string | Date | null): string {
  if (!dateStr) return 'Nunca'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'Fecha inválida'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSec < 45) return 'Hace unos segundos'
  if (diffMin < 60) return `Hace ${diffMin} min`
  if (diffHours < 24) return `Hace ${diffHours} h`
  if (diffDays === 1) return 'Ayer'
  if (diffDays < 7) return `Hace ${diffDays} días`
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatExactDateTime(dateStr?: string | Date | null): string {
  if (!dateStr) return '—'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '—'
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getActionLabel(action: string): { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'primary' } {
  switch (action) {
    case 'USER_LOGIN':
      return { label: 'Inicio de Sesión Exitoso', variant: 'success' }
    case 'USER_LOGIN_2FA':
      return { label: 'Login con 2FA Exitoso', variant: 'primary' }
    case 'USER_LOGIN_FAILED':
      return { label: 'Intento de Login Fallido', variant: 'danger' }
    case 'USER_LOGIN_2FA_FAILED':
      return { label: 'Fallo Verificación 2FA', variant: 'danger' }
    case 'USER_LOGIN_2FA_CHALLENGE':
      return { label: 'Reto 2FA Enviado', variant: 'warning' }
    case 'USER_STATUS_TOGGLED':
      return { label: 'Estado de Cuenta Modificado', variant: 'warning' }
    case 'USER_PASSWORD_RESET':
      return { label: 'Contraseña Restablecida', variant: 'warning' }
    case 'USER_PASSWORD_CHANGED':
      return { label: 'Contraseña Modificada', variant: 'info' }
    case 'USER_CREATED':
      return { label: 'Usuario Creado', variant: 'primary' }
    case 'CASE_AUTO_CREATED':
      return { label: 'Caso Creado por IA', variant: 'primary' }
    case 'CASE_STATUS_CHANGED':
      return { label: 'Estado de Caso Cambiado', variant: 'info' }
    case 'CASE_CRITICALITY_DOWNGRADED':
      return { label: 'Criticidad Modificada', variant: 'warning' }
    case 'EMAIL_THREAD_ASSOCIATED':
      return { label: 'Hilo de Email Asociado', variant: 'default' }
    case 'ALERTS_ESCALATED':
      return { label: 'Alerta Escalada', variant: 'danger' }
    case 'CLEANUP_CLASSIFICATION':
      return { label: 'Limpieza de Clasificación', variant: 'danger' }
    case 'CRITICIDAD_KB_RELOADED':
      return { label: 'Reglas de Criticidad Recargadas', variant: 'info' }
    default:
      return { label: action.replace(/_/g, ' '), variant: 'default' }
  }
}

export function AccountActivitySection() {
  const api = useApi()
  const showSuccess = useSuccessToast()
  const showError = useErrorToast()

  const [activeTab, setActiveTab] = useState<'accounts' | 'logins' | 'audit'>('accounts')
  const [isLoadingSummary, setIsLoadingSummary] = useState(true)
  const [isLoadingUsers, setIsLoadingUsers] = useState(true)
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)

  const [summary, setSummary] = useState<ActivitySummary | null>(null)
  const [users, setUsers] = useState<ActivityUser[]>([])
  
  // Filtros de Auditoría
  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([])
  const [auditPage, setAuditPage] = useState(1)
  const [auditTotalPages, setAuditTotalPages] = useState(1)
  const [auditTotal, setAuditTotal] = useState(0)
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [userFilter, setUserFilter] = useState('ALL')
  const [searchFilter, setSearchFilter] = useState('')

  // Filtros de Cuentas
  const [accountSearch, setAccountSearch] = useState('')
  const [accountRoleFilter, setAccountRoleFilter] = useState('ALL')

  // Filtros de Inicios de sesión
  const [loginUserFilter, setLoginUserFilter] = useState('ALL')
  const [loginPage, setLoginPage] = useState(1)
  const [loginTotalPages, setLoginTotalPages] = useState(1)
  const [loginLogs, setLoginLogs] = useState<AuditLogItem[]>([])

  // Modal para ver metadata
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null)
  const [copiedJson, setCopiedJson] = useState(false)

  // Cargar resumen
  const loadSummary = useCallback(async () => {
    try {
      setIsLoadingSummary(true)
      const res = await api.get<ActivitySummary>('/admin/activity/summary')
      setSummary(res)
    } catch (err: any) {
      console.error('Error cargando resumen de actividad:', err)
    } finally {
      setIsLoadingSummary(false)
    }
  }, [api])

  // Cargar lista de cuentas
  const loadUsers = useCallback(async () => {
    try {
      setIsLoadingUsers(true)
      const res = await api.get<ActivityUser[]>('/admin/activity/users')
      setUsers(res || [])
    } catch (err: any) {
      console.error('Error cargando usuarios:', err)
      showError('Error', 'No se pudo cargar la actividad de las cuentas')
    } finally {
      setIsLoadingUsers(false)
    }
  }, [api, showError])

  // Cargar logs de auditoría
  const loadAuditLogs = useCallback(async (page = 1) => {
    try {
      setIsLoadingLogs(true)
      const query = new URLSearchParams({
        page: String(page),
        limit: '30',
      })
      if (categoryFilter !== 'ALL') query.set('category', categoryFilter)
      if (userFilter !== 'ALL') query.set('userId', userFilter)
      if (searchFilter.trim()) query.set('search', searchFilter.trim())

      const res = await api.get<any>(`/admin/activity/logs?${query.toString()}`)
      setAuditLogs(res.logs || [])
      setAuditTotalPages(res.totalPages || 1)
      setAuditTotal(res.total || 0)
      setAuditPage(res.page || 1)
    } catch (err: any) {
      console.error('Error cargando logs:', err)
    } finally {
      setIsLoadingLogs(false)
    }
  }, [api, categoryFilter, userFilter, searchFilter])

  // Cargar historial de logins
  const loadLoginLogs = useCallback(async (page = 1) => {
    try {
      setIsLoadingLogs(true)
      const query = new URLSearchParams({
        page: String(page),
        limit: '30',
      })
      if (loginUserFilter !== 'ALL') query.set('userId', loginUserFilter)

      const res = await api.get<any>(`/admin/activity/logins?${query.toString()}`)
      setLoginLogs(res.logs || [])
      setLoginTotalPages(res.totalPages || 1)
      setLoginPage(res.page || 1)
    } catch (err: any) {
      console.error('Error cargando historial de login:', err)
    } finally {
      setIsLoadingLogs(false)
    }
  }, [api, loginUserFilter])

  useEffect(() => {
    loadSummary()
    loadUsers()
  }, [loadSummary, loadUsers])

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs(auditPage)
    } else if (activeTab === 'logins') {
      loadLoginLogs(loginPage)
    }
  }, [activeTab, loadAuditLogs, loadLoginLogs, auditPage, loginPage])

  const handleRefreshAll = async () => {
    await Promise.all([
      loadSummary(),
      loadUsers(),
      activeTab === 'audit' ? loadAuditLogs(1) : Promise.resolve(),
      activeTab === 'logins' ? loadLoginLogs(1) : Promise.resolve(),
    ])
    showSuccess('Actualizado', 'Datos de actividad sincronizados')
  }

  // Filtrado en memoria de cuentas
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const matchSearch =
        accountSearch === '' ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(accountSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(accountSearch.toLowerCase()) ||
        (u.area?.name || '').toLowerCase().includes(accountSearch.toLowerCase())
      
      const matchRole = accountRoleFilter === 'ALL' || u.role === accountRoleFilter
      return matchSearch && matchRole
    })
  }, [users, accountSearch, accountRoleFilter])

  // Exportar auditoría a CSV
  const handleExportCSV = () => {
    try {
      const rows = activeTab === 'logins' ? loginLogs : auditLogs
      if (rows.length === 0) {
        showError('Exportar', 'No hay registros para exportar')
        return
      }

      const headers = ['ID', 'Fecha_Hora', 'Usuario_Nombre', 'Usuario_Email', 'Rol', 'Accion', 'Entidad', 'IP', 'Dispositivo']
      const csvContent = [
        headers.join(','),
        ...rows.map(log => {
          const u = log.user
          const ua = parseUserAgent(log.userAgent)
          return [
            `"${log.id}"`,
            `"${formatExactDateTime(log.timestamp)}"`,
            `"${u ? `${u.firstName} ${u.lastName}` : 'Sistema'}"`,
            `"${u?.email || 'N/A'}"`,
            `"${u?.role || 'N/A'}"`,
            `"${log.action}"`,
            `"${log.entityType || 'N/A'}"`,
            `"${log.ipAddress || 'N/A'}"`,
            `"${ua.browser} (${ua.os})"`,
          ].join(',')
        }),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `auditoria_actividad_${new Date().toISOString().slice(0, 10)}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      showSuccess('Exportado', 'Archivo CSV descargado exitosamente')
    } catch (err: any) {
      showError('Error', 'No se pudo exportar el archivo CSV')
    }
  }

  const copyMetadataJson = () => {
    if (!selectedLog) return
    navigator.clipboard.writeText(JSON.stringify(selectedLog, null, 2))
    setCopiedJson(true)
    setTimeout(() => setCopiedJson(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Monitoreo y Registro de Actividad
            </h2>
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
              Solo Administrador
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Supervisa los inicios de sesión, sesiones activas y la trazabilidad de operaciones de las cuentas del sistema.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="w-4 h-4" />}
          >
            Exportar CSV
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefreshAll}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoadingSummary || isLoadingUsers ? 'animate-spin' : ''}`} />}
          >
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Cuentas */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total Cuentas
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary?.totalUsers ?? '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{summary?.activeUsers ?? 0}</span> activas · {summary?.inactiveUsers ?? 0} inactivas
          </p>
        </div>

        {/* Conectados Hoy */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Conectados Hoy
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <UserCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary?.loggedInToday ?? '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Usuarios con login registrado hoy
          </p>
        </div>

        {/* Logins 24h */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Logins (Últimas 24h)
            </span>
            <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <LogIn className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary?.logins24h ?? '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Accesos autenticados
          </p>
        </div>

        {/* Seguridad 2FA */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Seguridad 2FA
            </span>
            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary ? `${summary.usersWith2FA} / ${summary.totalUsers}` : '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {summary && summary.totalUsers > 0
              ? `${Math.round((summary.usersWith2FA / summary.totalUsers) * 100)}% con 2FA habilitado`
              : 'Verificación en dos pasos'}
          </p>
        </div>

        {/* Actividades Totales */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Total Actividades
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
              <Activity className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {summary?.totalLogs ?? '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span className="font-semibold text-gray-700 dark:text-gray-300">+{summary?.recentLogsCount ?? 0}</span> en los últimos 7 días
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('accounts')}
          className={`flex items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'accounts'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <Users className="w-4 h-4" />
          Cuentas y Último Acceso ({users.length})
        </button>

        <button
          onClick={() => setActiveTab('logins')}
          className={`flex items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'logins'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <LogIn className="w-4 h-4" />
          Historial de Inicios de Sesión
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          <Activity className="w-4 h-4" />
          Registro de Auditoría ({auditTotal})
        </button>
      </div>

      {/* TAB 1: CUENTAS Y ÚLTIMO ACCESO */}
      {activeTab === 'accounts' && (
        <div className="space-y-4">
          {/* Filtros de cuentas */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, correo o área..."
                value={accountSearch}
                onChange={e => setAccountSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="w-full sm:w-48">
              <select
                value={accountRoleFilter}
                onChange={e => setAccountRoleFilter(e.target.value)}
                className="w-full py-2 px-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ALL">Todos los roles</option>
                <option value="ADMIN">ADMIN</option>
                <option value="MANAGER">MANAGER</option>
                <option value="OPERATOR">OPERATOR</option>
                <option value="HR">HR</option>
                <option value="FINANCE">FINANCE</option>
                <option value="LEGAL">LEGAL</option>
              </select>
            </div>
          </div>

          {/* Tabla de cuentas */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Usuario</th>
                    <th className="px-4 py-3 font-semibold">Rol y Área</th>
                    <th className="px-4 py-3 font-semibold">Estado</th>
                    <th className="px-4 py-3 font-semibold">Seguridad 2FA</th>
                    <th className="px-4 py-3 font-semibold">Último Inicio de Sesión</th>
                    <th className="px-4 py-3 font-semibold">Última IP / Dispositivo</th>
                    <th className="px-4 py-3 font-semibold text-center">Actividades</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No se encontraron cuentas con los criterios de búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(u => {
                      const ua = parseUserAgent(u.lastLoginUserAgent)
                      const isRecentLogin = u.lastLogin && new Date().getTime() - new Date(u.lastLogin).getTime() < 24 * 60 * 60 * 1000

                      return (
                        <tr key={u.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 font-bold flex items-center justify-center text-xs flex-shrink-0">
                                {u.firstName?.charAt(0) || 'U'}{u.lastName?.charAt(0) || ''}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {u.firstName} {u.lastName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {u.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 w-fit">
                                {u.role}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[140px]">
                                {u.area?.name || 'Sin área'}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {u.isActive ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Activo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                Inactivo
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {u.twoFactorEnabled ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 dark:text-purple-300">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                Habilitado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                <ShieldAlert className="w-3.5 h-3.5" />
                                No configurado
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                <span className={`text-xs font-medium ${isRecentLogin ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-gray-200'}`}>
                                  {formatRelativeTime(u.lastLogin)}
                                </span>
                              </div>
                              {u.lastLogin && (
                                <span className="text-[11px] text-gray-400 ml-5">
                                  {formatExactDateTime(u.lastLogin)}
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {u.lastLoginIp ? (
                              <div className="flex flex-col text-xs">
                                <span className="font-mono text-gray-700 dark:text-gray-300">
                                  {u.lastLoginIp}
                                </span>
                                <span className="text-[11px] text-gray-400">
                                  {ua.browser} · {ua.os}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <button
                              onClick={() => {
                                setUserFilter(u.id)
                                setActiveTab('audit')
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 transition-colors"
                            >
                              <Activity className="w-3.5 h-3.5" />
                              {u.totalActivities} logs
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: HISTORIAL DE INICIOS DE SESIÓN */}
      {activeTab === 'logins' && (
        <div className="space-y-4">
          {/* Filtros de logins */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Filtrar por Usuario:
              </label>
              <select
                value={loginUserFilter}
                onChange={e => {
                  setLoginUserFilter(e.target.value)
                  setLoginPage(1)
                }}
                className="py-1.5 px-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ALL">Todos los usuarios</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadLoginLogs(loginPage)}
              leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            >
              Recargar logins
            </Button>
          </div>

          {/* Tabla de logins */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Fecha y Hora</th>
                    <th className="px-4 py-3 font-semibold">Usuario</th>
                    <th className="px-4 py-3 font-semibold">Evento</th>
                    <th className="px-4 py-3 font-semibold">Dirección IP</th>
                    <th className="px-4 py-3 font-semibold">Dispositivo / Navegador</th>
                    <th className="px-4 py-3 font-semibold">Detalles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {loginLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No hay inicios de sesión registrados todavía.
                      </td>
                    </tr>
                  ) : (
                    loginLogs.map(log => {
                      const ua = parseUserAgent(log.userAgent)
                      const isSuccess = log.action === 'USER_LOGIN' || log.action === 'USER_LOGIN_2FA'
                      const isFailed = log.action === 'USER_LOGIN_FAILED' || log.action === 'USER_LOGIN_2FA_FAILED'

                      return (
                        <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {formatExactDateTime(log.timestamp)}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                {formatRelativeTime(log.timestamp)}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {log.user ? (
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {log.user.firstName} {log.user.lastName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {log.user.email}
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {log.metadata?.attemptedEmail || 'Usuario anónimo'}
                                </p>
                                <p className="text-xs text-gray-400">Sin cuenta asociada</p>
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {isSuccess ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                                {log.action === 'USER_LOGIN_2FA' ? '2FA Exitoso' : 'Login Exitoso'}
                              </span>
                            ) : isFailed ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                                <XCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400" />
                                Intento Fallido
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                Reto 2FA
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                              {log.ipAddress || '—'}
                            </span>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                              <Monitor className="w-3.5 h-3.5 text-gray-400" />
                              <span>{ua.browser} ({ua.os})</span>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {loginTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Página {loginPage} de {loginTotalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loginPage <= 1}
                    onClick={() => setLoginPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loginPage >= loginTotalPages}
                    onClick={() => setLoginPage(p => Math.min(loginTotalPages, p + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: REGISTRO DE AUDITORÍA COMPLETO */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por texto, IP, entidad..."
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadAuditLogs(1)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <select
                value={categoryFilter}
                onChange={e => {
                  setCategoryFilter(e.target.value)
                  setAuditPage(1)
                }}
                className="w-full py-2 px-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ALL">Todas las categorías</option>
                <option value="AUTH">Autenticación e Inicios de Sesión</option>
                <option value="CASES">Gestión de Casos</option>
                <option value="EMAILS">Correos y Clasificación</option>
                <option value="ALERTS">Alertas y Escalamientos</option>
                <option value="USERS">Gestión de Usuarios</option>
                <option value="CONFIG">Configuración del Sistema</option>
              </select>
            </div>

            <div>
              <select
                value={userFilter}
                onChange={e => {
                  setUserFilter(e.target.value)
                  setAuditPage(1)
                }}
                className="w-full py-2 px-3 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="ALL">Todos los usuarios</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabla de Auditoría */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Fecha y Hora</th>
                    <th className="px-4 py-3 font-semibold">Responsable</th>
                    <th className="px-4 py-3 font-semibold">Acción</th>
                    <th className="px-4 py-3 font-semibold">Entidad</th>
                    <th className="px-4 py-3 font-semibold">IP / Origen</th>
                    <th className="px-4 py-3 font-semibold text-right">Detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No se encontraron registros de auditoría para los filtros aplicados.
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map(log => {
                      const actionInfo = getActionLabel(log.action)

                      return (
                        <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {formatExactDateTime(log.timestamp)}
                              </span>
                              <span className="text-[11px] text-gray-400">
                                {formatRelativeTime(log.timestamp)}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            {log.user ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold flex items-center justify-center text-[10px]">
                                  {log.user.firstName?.charAt(0) || 'U'}
                                </div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {log.user.firstName} {log.user.lastName}
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                Sistema Automático
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                actionInfo.variant === 'success'
                                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                  : actionInfo.variant === 'danger'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                  : actionInfo.variant === 'warning'
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                  : actionInfo.variant === 'primary'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {actionInfo.label}
                            </span>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                              <span className="font-semibold text-gray-900 dark:text-gray-100">
                                {log.entityType || '—'}
                              </span>
                              {log.entityId && (
                                <span className="font-mono text-[11px] text-gray-400 truncate max-w-[120px]">
                                  ({log.entityId.slice(0, 8)}...)
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                              {log.ipAddress || 'Interno'}
                            </span>
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Inspeccionar
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación Auditoría */}
            {auditTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Mostrando página {auditPage} de {auditTotalPages} ({auditTotal} registros en total)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditPage <= 1}
                    onClick={() => {
                      const next = Math.max(1, auditPage - 1)
                      setAuditPage(next)
                      loadAuditLogs(next)
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditPage >= auditTotalPages}
                    onClick={() => {
                      const next = Math.min(auditTotalPages, auditPage + 1)
                      setAuditPage(next)
                      loadAuditLogs(next)
                    }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Inspector de Auditoría */}
      {selectedLog && (
        <Modal
          isOpen={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Detalle del Evento de Auditoría"
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800 text-xs">
              <div>
                <span className="text-gray-500 dark:text-gray-400">ID del Evento:</span>
                <p className="font-mono font-medium text-gray-900 dark:text-gray-100">{selectedLog.id}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Fecha y Hora:</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatExactDateTime(selectedLog.timestamp)}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Acción:</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{selectedLog.action}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Entidad:</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{selectedLog.entityType} ({selectedLog.entityId || 'N/A'})</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">IP:</span>
                <p className="font-mono font-medium text-gray-900 dark:text-gray-100">{selectedLog.ipAddress || 'No registrada'}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Usuario Responsable:</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {selectedLog.user ? `${selectedLog.user.firstName} ${selectedLog.user.lastName} (${selectedLog.user.email})` : 'Sistema Automático'}
                </p>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Metadata y Cambios Asociados (JSON):
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyMetadataJson}
                  leftIcon={copiedJson ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                >
                  {copiedJson ? 'Copiado' : 'Copiar'}
                </Button>
              </div>

              <pre className="p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto max-h-72 font-mono">
                {JSON.stringify(
                  {
                    changes: selectedLog.changes,
                    metadata: selectedLog.metadata,
                    userAgent: selectedLog.userAgent,
                  },
                  null,
                  2
                )}
              </pre>
            </div>

            <div className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-800">
              <Button variant="secondary" size="sm" onClick={() => setSelectedLog(null)}>
                Cerrar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
